// Tab "Configuración" de Informes en Monitor.
//
// Replica lo que hace ConfigInformes.qml en tc_qml: gestión de emails
// verificados (alta/baja/verify/alias/hidden) + frecuencias y destinatarios
// por cada tipo de informe automático + toggle de cierre automático.
//
// Patrón draft + baseline + localStorage igual que Tarifario/Permisos.
// Al "Guardar cambios" se POSTea a /monitor/reports-config-update.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Button,
  Spinner,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import { isEqual } from "lodash";
import { auth } from "../../firebase";
import VerifyEmailModal from "./VerifyEmailModal";

const TCSOCKET_URL =
  process.env.REACT_APP_TCSOCKET_URL || "https://api.ticketcontrol.net";

// Tipos de informe automático. Cada uno tiene un `base` que se usa como
// prefijo en las keys de email_config.options: `<base>_<freq>`, `<base>_to`, `<base>_cc`.
const REPORT_TYPES = [
  { base: "resumen", label: "Resumen General", icon: "summarize", description: "Resumen general de la operación" },
  { base: "cash_movements", label: "Movimientos de Caja", icon: "receipt_long", description: "Ingresos y egresos de la caja" },
  { base: "tickets", label: "Tickets Cerrados", icon: "confirmation_number", description: "Detalle de tickets cerrados" },
  { base: "mensuales", label: "Abonados", icon: "event_available", description: "Estado de cuentas de abonados" },
  { base: "inout", label: "Entradas / Salidas", icon: "swap_horiz", description: "Entradas y salidas de vehículos" },
  { base: "invoices", label: "Facturación", icon: "description", description: "Facturas y recibos emitidos" },
  { base: "deals", label: "Acuerdos", icon: "handshake", description: "Operaciones con acuerdos empresariales" },
];

const FREQUENCIES = [
  { key: "diario", label: "Diario" },
  { key: "semanal", label: "Semanal" },
  { key: "mensual", label: "Mensual" },
];

const DRAFT_STORAGE_KEY = (key) => `informes_config_draft_${key}`;

// Construye el draft inicial a partir del app_config.email_config.options
// y las options que no sean relevantes se ignoran — nos interesan solo las
// keys mapeadas por REPORT_TYPES + cash_close.
function buildInitialDraft(options) {
  const opts = options || {};
  const draft = {};
  for (const rt of REPORT_TYPES) {
    for (const freq of FREQUENCIES) {
      draft[`${rt.base}_${freq.key}`] = opts[`${rt.base}_${freq.key}`] ? 1 : 0;
    }
    draft[`${rt.base}_to`] = String(opts[`${rt.base}_to`] || "");
    draft[`${rt.base}_cc`] = String(opts[`${rt.base}_cc`] || "");
  }
  draft.cash_close = opts.cash_close ? 1 : 0;
  draft.cash_close_to = String(opts.cash_close_to || "");
  draft.cash_close_cc = String(opts.cash_close_cc || "");
  return draft;
}

// Parsea un string CSV de emails a un array normalizado (lowercase, sin
// duplicados, sin vacíos).
function parseCsvEmails(csv) {
  if (!csv) return [];
  return Array.from(
    new Set(
      csv
        .split(",")
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
    )
  );
}

function joinCsvEmails(emails) {
  return (emails || []).join(", ");
}

const ConfigTab = ({ machineKey, appConfig, reportEmails, canEdit }) => {
  const options = appConfig?.email_config?.options || {};
  const [draft, setDraft] = useState(() => buildInitialDraft(options));
  const [sending, setSending] = useState(false);
  const lastSyncedRef = useRef(options);
  const lastSyncedDraftRef = useRef(buildInitialDraft(options));

  // Email verification modal
  const [verifyingEmail, setVerifyingEmail] = useState(null);

  // Add-email field
  const [newEmail, setNewEmail] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);

  // Sync draft con Firestore cuando llega un snapshot nuevo.
  useEffect(() => {
    const newSnapshot = buildInitialDraft(options);
    if (!canEdit) {
      setDraft(newSnapshot);
      lastSyncedRef.current = options;
      lastSyncedDraftRef.current = newSnapshot;
      return;
    }
    // Restaurar draft de localStorage solo en mount inicial.
    const saved = localStorage.getItem(DRAFT_STORAGE_KEY(machineKey));
    if (saved && lastSyncedDraftRef.current === null) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && isEqual(parsed.base, newSnapshot)) {
          setDraft(parsed.draft);
          lastSyncedRef.current = options;
          lastSyncedDraftRef.current = newSnapshot;
          return;
        }
      } catch (_) {}
      localStorage.removeItem(DRAFT_STORAGE_KEY(machineKey));
    }
    // Si el snapshot cambió y el usuario no tenía edits locales, auto-refrescar.
    if (!isEqual(options, lastSyncedRef.current)) {
      const hadLocalEdits = !isEqual(draft, lastSyncedDraftRef.current);
      if (!hadLocalEdits) {
        setDraft(newSnapshot);
      } else {
        toast.info(
          "La máquina actualizó la configuración de informes. Tenés cambios locales sin guardar.",
          { autoClose: 6000 }
        );
      }
      lastSyncedRef.current = options;
      lastSyncedDraftRef.current = newSnapshot;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, canEdit, machineKey]);

  // Persistir draft en localStorage.
  useEffect(() => {
    if (!canEdit || !machineKey) return;
    const snap = lastSyncedDraftRef.current;
    if (snap && isEqual(draft, snap)) {
      localStorage.removeItem(DRAFT_STORAGE_KEY(machineKey));
    } else {
      try {
        localStorage.setItem(
          DRAFT_STORAGE_KEY(machineKey),
          JSON.stringify({ draft, base: snap })
        );
      } catch (_) {}
    }
  }, [draft, canEdit, machineKey]);

  const isDirty = useMemo(() => {
    return !isEqual(draft, lastSyncedDraftRef.current);
  }, [draft]);

  const verifiedEmails = useMemo(
    () => (reportEmails || []).filter((e) => e.verified).map((e) => e.email.toLowerCase()),
    [reportEmails]
  );

  const toggleFreq = (base, freq) => {
    if (!canEdit) return;
    const k = `${base}_${freq}`;
    setDraft((d) => ({ ...d, [k]: d[k] ? 0 : 1 }));
  };

  const toggleEmailInCsv = (base, field, email) => {
    if (!canEdit) return;
    const k = `${base}_${field}`;
    const list = parseCsvEmails(draft[k]);
    const idx = list.indexOf(email.toLowerCase());
    const next = [...list];
    if (idx === -1) next.push(email.toLowerCase());
    else next.splice(idx, 1);
    setDraft((d) => ({ ...d, [k]: joinCsvEmails(next) }));
  };

  // ── Acciones sobre emails (HTTP) ──
  const emailAction = async (payload) => {
    if (!canEdit) return { ok: false };
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Sesión expirada. Volvé a iniciar sesión.");
        return { ok: false };
      }
      const token = await user.getIdToken();
      const res = await fetch(`${TCSOCKET_URL}/monitor/reports-emails-update`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key: machineKey, payload }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || "Error al aplicar la operación");
        return { ok: false, error: data };
      }
      return { ok: true, data };
    } catch (err) {
      toast.error(`Error de red: ${err.message || err}`);
      return { ok: false };
    }
  };

  const handleAddEmail = async () => {
    const email = (newEmail || "").trim().toLowerCase();
    if (!email) return;
    if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(email)) {
      toast.error("Email inválido");
      return;
    }
    setAddingEmail(true);
    const res = await emailAction({ action: "add", email });
    setAddingEmail(false);
    if (res.ok) {
      setNewEmail("");
      toast.info("Se envió un código de verificación. Pedile al destinatario que te lo pase.");
      setVerifyingEmail(email);
    }
  };

  const handleRemoveEmail = async (id, email) => {
    if (!window.confirm(`¿Eliminar ${email} de la lista de destinatarios?`)) return;
    await emailAction({ action: "remove", id });
  };

  const handleUpdateAlias = async (id, alias, hidden) => {
    await emailAction({ action: "update", id, alias, hidden: !!hidden });
  };

  const handleResend = async (email) => {
    const res = await emailAction({ action: "resend", email });
    if (res.ok) {
      toast.info("Código reenviado");
      setVerifyingEmail(email);
    }
  };

  // ── Guardar configuración ──
  const handleSave = async () => {
    if (!canEdit || !isDirty) return;
    setSending(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Sesión expirada. Volvé a iniciar sesión.");
        setSending(false);
        return;
      }
      const token = await user.getIdToken();
      const res = await fetch(`${TCSOCKET_URL}/monitor/reports-config-update`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key: machineKey,
          payload: { options: draft },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || "No se pudo aplicar la configuración");
        if (Array.isArray(data.errors) && data.errors.length) {
          console.warn("[ConfigTab] errors:", data.errors);
        }
        setSending(false);
        return;
      }
      toast.success("Configuración aplicada en la máquina");
      localStorage.removeItem(DRAFT_STORAGE_KEY(machineKey));
      // Actualizar baseline para que el próximo onSnapshot no marque espurio
      // "cambios sin guardar". El draft ya es igual a lo que se envió.
      lastSyncedDraftRef.current = { ...draft };
      // Forzar re-render para que isDirty (useMemo sobre draft) se recompute
      // contra el nuevo baseline. Clonar mantiene el contenido.
      setDraft((d) => ({ ...d }));
    } catch (err) {
      toast.error(`Error de red: ${err.message || err}`);
    } finally {
      setSending(false);
    }
  };

  const handleDiscard = () => {
    setDraft(buildInitialDraft(options));
    localStorage.removeItem(DRAFT_STORAGE_KEY(machineKey));
  };

  const senderInfo = useMemo(() => {
    const ec = appConfig?.email_config || {};
    const ownEmail = ec.email;
    const status = ec.status;
    const emailModule = appConfig?.modules?.email;
    if (status === 1 && emailModule === 1 && ownEmail) {
      return `Los informes se envían desde ${ownEmail} (email propio)`;
    }
    return "Los informes se envían desde informes@mail.ticketcontrol.net (TicketControl)";
  }, [appConfig]);

  return (
    <>
      {/* Sender info */}
      <Card className="mb-3" style={{ border: "1px solid #eee" }}>
        <CardBody className="py-2 px-3 d-flex align-items-center gap-2">
          <span className="material-symbols-rounded" style={{ fontSize: 20, color: "#50a5f1" }}>
            mail
          </span>
          <div className="small text-muted">{senderInfo}</div>
        </CardBody>
      </Card>

      {/* Emails destinatarios */}
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="material-symbols-rounded" style={{ fontSize: 22, color: "#50a5f1" }}>
              group
            </span>
            <h6 className="mb-0">Emails destinatarios</h6>
          </div>
          <p className="text-muted small mb-3">
            Agregá emails y verificalos para empezar a recibir informes.
          </p>

          {canEdit && (
            <Row className="g-2 mb-3 align-items-stretch">
              <Col>
                <Input
                  type="email"
                  placeholder="ejemplo@email.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
                  disabled={addingEmail}
                />
              </Col>
              <Col xs="auto">
                <Button
                  color="primary"
                  onClick={handleAddEmail}
                  disabled={addingEmail || !newEmail.trim()}
                >
                  {addingEmail ? <Spinner size="sm" /> : (
                    <>
                      <span className="material-symbols-rounded align-middle me-1" style={{ fontSize: 18 }}>
                        add
                      </span>
                      Agregar
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          )}

          {(!reportEmails || reportEmails.length === 0) ? (
            <div className="text-muted small text-center py-3">
              No hay emails registrados. Agregá un email para comenzar a recibir informes.
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {reportEmails.map((e) => (
                <EmailRow
                  key={e.id}
                  emailRow={e}
                  canEdit={canEdit}
                  onRemove={() => handleRemoveEmail(e.id, e.email)}
                  onUpdateAlias={(alias, hidden) => handleUpdateAlias(e.id, alias, hidden)}
                  onResend={() => handleResend(e.email)}
                  onVerifyOpen={() => setVerifyingEmail(e.email)}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Cards por tipo de informe */}
      {REPORT_TYPES.map((rt) => (
        <ReportTypeCard
          key={rt.base}
          reportType={rt}
          draft={draft}
          verifiedEmails={reportEmails || []}
          canEdit={canEdit}
          onToggleFreq={(freq) => toggleFreq(rt.base, freq)}
          onToggleEmail={(email) => toggleEmailInCsv(rt.base, "to", email)}
        />
      ))}

      {/* Cierre de caja automático */}
      <Card
        className="mb-3 shadow-sm"
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <CardBody className="py-3">
          <Row className="align-items-center g-3">
            <Col md={6} className="d-flex align-items-center gap-2">
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 22, color: "#fd7e14", flexShrink: 0 }}
              >
                point_of_sale
              </span>
              <div>
                <h6 className="mb-0">Cierre de Caja</h6>
                <div className="text-muted small">
                  Se envía automáticamente al realizar un cierre de caja
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex align-items-center gap-2 justify-content-md-end">
                <Input
                  type="checkbox"
                  id="cash_close_toggle"
                  checked={!!draft.cash_close}
                  disabled={!canEdit}
                  onChange={() =>
                    canEdit &&
                    setDraft((d) => ({
                      ...d,
                      cash_close: d.cash_close ? 0 : 1,
                    }))
                  }
                />
                <label htmlFor="cash_close_toggle" className="small mb-0">
                  Envío automático al cerrar caja
                </label>
              </div>
            </Col>
          </Row>
        </CardBody>

        <div
          style={{
            background: "#f8fafc",
            borderTop: "1px solid #eef2f6",
            padding: "12px 20px",
          }}
        >
          <EmailChipsRow
            label="Destinatarios"
            csv={draft.cash_close_to}
            verifiedEmails={reportEmails || []}
            canEdit={canEdit}
            onToggle={(email) => {
              const list = parseCsvEmails(draft.cash_close_to);
              const idx = list.indexOf(email.toLowerCase());
              const next = [...list];
              if (idx === -1) next.push(email.toLowerCase());
              else next.splice(idx, 1);
              setDraft((d) => ({
                ...d,
                cash_close_to: joinCsvEmails(next),
              }));
            }}
          />
        </div>
      </Card>

      {/* Barra de acción flotante (fixed al bottom viewport, igual que Tarifario) */}
      {canEdit && isDirty && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            background: "#2a3042",
            color: "#fff",
            borderTop: "1px solid #1b1e2c",
            boxShadow: "0 -4px 16px rgba(0,0,0,0.25)",
            padding: "14px 24px",
            zIndex: 1030,
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <span
            className="material-symbols-rounded"
            style={{ fontSize: 24, color: "#ffd76b" }}
          >
            edit_note
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: "#fff", fontSize: 14 }}>
              Tenés cambios sin guardar
            </div>
            <div style={{ fontSize: 12, color: "#a8b0c3" }}>
              Los cambios se aplicarán en la máquina del estacionamiento al
              enviar. Asegurate de que tenga conexión a internet.
            </div>
          </div>
          <Button
            color="link"
            onClick={handleDiscard}
            disabled={sending}
            style={{
              textDecoration: "none",
              color: "#ced4e0",
              fontWeight: 500,
            }}
          >
            Descartar
          </Button>
          <Button
            color="success"
            onClick={handleSave}
            disabled={sending}
            style={{ fontWeight: 600, padding: "8px 20px" }}
          >
            {sending ? (
              <>
                <Spinner size="sm" className="me-1" /> Enviando...
              </>
            ) : (
              <>
                <span
                  className="material-symbols-rounded align-middle me-1"
                  style={{ fontSize: 16 }}
                >
                  send
                </span>
                Enviar cambios
              </>
            )}
          </Button>
        </div>
      )}
      {/* Spacer para que la barra fixed no tape el último card al scrollear. */}
      {canEdit && isDirty && <div style={{ height: 80 }} />}

      <VerifyEmailModal
        isOpen={!!verifyingEmail}
        email={verifyingEmail || ""}
        machineKey={machineKey}
        onClose={() => setVerifyingEmail(null)}
        onVerified={() => setVerifyingEmail(null)}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────
// Row de un email en la lista
// ─────────────────────────────────────────────────────────
const EmailRow = ({ emailRow, canEdit, onRemove, onUpdateAlias, onResend, onVerifyOpen }) => {
  const [alias, setAlias] = useState(emailRow.alias || "");
  const [hidden, setHidden] = useState(!!emailRow.hidden);
  const [editing, setEditing] = useState(false);
  const verified = !!emailRow.verified;

  useEffect(() => {
    setAlias(emailRow.alias || "");
    setHidden(!!emailRow.hidden);
  }, [emailRow.alias, emailRow.hidden]);

  const save = () => {
    onUpdateAlias(alias.trim(), hidden);
    setEditing(false);
  };

  // Modo edición: todo en una sola fila flexible, centrado vertical.
  if (editing) {
    return (
      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 8,
          padding: "10px 12px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <span
          className="material-symbols-rounded"
          style={{ fontSize: 20, color: "#9aa5b1" }}
        >
          person
        </span>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <b style={{ fontSize: 14 }}>{emailRow.email}</b>
          {verified ? (
            <Badge color="success" pill style={{ alignSelf: "flex-start" }}>
              Verificado
            </Badge>
          ) : (
            <Badge color="warning" pill style={{ alignSelf: "flex-start" }}>
              Pendiente
            </Badge>
          )}
        </div>
        <Input
          bsSize="sm"
          type="text"
          value={alias}
          maxLength={80}
          onChange={(e) => setAlias(e.target.value)}
          placeholder="Alias (opcional)"
          style={{ flex: 1, minWidth: 160 }}
        />
        <div className="d-flex align-items-center gap-2">
          <Input
            type="checkbox"
            id={`hidden-${emailRow.id}`}
            checked={hidden}
            onChange={(e) => setHidden(e.target.checked)}
            style={{ marginTop: 0 }}
          />
          <label
            htmlFor={`hidden-${emailRow.id}`}
            className="small mb-0"
            style={{ whiteSpace: "nowrap" }}
          >
            Ocultar a operadores
          </label>
        </div>
        <div className="d-flex gap-1">
          <Button size="sm" color="success" onClick={save}>
            Guardar
          </Button>
          <Button
            size="sm"
            color="secondary"
            outline
            onClick={() => setEditing(false)}
          >
            Cancelar
          </Button>
          <Button size="sm" color="danger" outline onClick={onRemove}>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 16 }}
            >
              delete
            </span>
          </Button>
        </div>
      </div>
    );
  }

  // Modo display: email + badge | alias/visibilidad | botones.
  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 8,
        padding: "10px 12px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span
        className="material-symbols-rounded"
        style={{ fontSize: 20, color: "#9aa5b1" }}
      >
        person
      </span>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div className="d-flex align-items-center gap-2">
          <b style={{ fontSize: 14 }}>{emailRow.email}</b>
          {verified ? (
            <Badge color="success" pill>
              Verificado
            </Badge>
          ) : (
            <Badge color="warning" pill>
              Pendiente
            </Badge>
          )}
        </div>
        <div className="small text-muted mt-1">
          {emailRow.alias ? (
            <>
              Alias: <b>{emailRow.alias}</b> ·{" "}
            </>
          ) : null}
          {emailRow.hidden ? "Oculto a operadores" : "Visible para operadores"}
        </div>
      </div>
      {canEdit && (
        <div className="d-flex gap-1">
          {!verified && (
            <Button size="sm" color="warning" outline onClick={onVerifyOpen}>
              Verificar
            </Button>
          )}
          {!verified && (
            <Button size="sm" color="secondary" outline onClick={onResend}>
              Reenviar código
            </Button>
          )}
          <Button
            size="sm"
            color="primary"
            outline
            onClick={() => setEditing(true)}
          >
            Editar
          </Button>
          <Button size="sm" color="danger" outline onClick={onRemove}>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 16 }}
            >
              delete
            </span>
          </Button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Card de un tipo de informe (frecuencias + destinatarios)
// ─────────────────────────────────────────────────────────
const ReportTypeCard = ({ reportType, draft, verifiedEmails, canEdit, onToggleFreq, onToggleEmail }) => {
  const toCsv = draft[`${reportType.base}_to`] || "";

  return (
    <Card
      className="mb-3 shadow-sm"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      {/* Header: título + descripción + frecuencia (sin hr, solo padding) */}
      <CardBody className="py-3">
        <Row className="align-items-center g-3">
          <Col md={6} className="d-flex align-items-center gap-2">
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 22, color: "#50a5f1", flexShrink: 0 }}
            >
              {reportType.icon}
            </span>
            <div>
              <h6 className="mb-0">{reportType.label}</h6>
              <div className="text-muted small">{reportType.description}</div>
            </div>
          </Col>
          <Col md={6}>
            <div className="d-flex align-items-center flex-wrap gap-3 justify-content-md-end">
              <div className="small text-muted d-flex align-items-center">
                <span
                  className="material-symbols-rounded me-1"
                  style={{ fontSize: 14 }}
                >
                  schedule
                </span>
                Frecuencia:
              </div>
              {FREQUENCIES.map((f) => {
                const k = `${reportType.base}_${f.key}`;
                return (
                  <div key={f.key} className="d-flex align-items-center gap-1">
                    <Input
                      type="checkbox"
                      id={`${reportType.base}-${f.key}`}
                      checked={!!draft[k]}
                      disabled={!canEdit}
                      onChange={() => onToggleFreq(f.key)}
                    />
                    <label
                      htmlFor={`${reportType.base}-${f.key}`}
                      className="small mb-0"
                    >
                      {f.label}
                    </label>
                  </div>
                );
              })}
            </div>
          </Col>
        </Row>
      </CardBody>

      {/* Footer: destinatarios siempre visible — se pueden preconfigurar
          aunque no haya frecuencia activa. */}
      <div
        style={{
          background: "#f8fafc",
          borderTop: "1px solid #eef2f6",
          padding: "12px 20px",
        }}
      >
        <EmailChipsRow
          label="Destinatarios"
          csv={toCsv}
          verifiedEmails={verifiedEmails}
          canEdit={canEdit}
          onToggle={onToggleEmail}
        />
      </div>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────
// Fila de chips de emails verificados, con toggle por chip
// ─────────────────────────────────────────────────────────
const EmailChipsRow = ({ label, csv, verifiedEmails, canEdit, onToggle }) => {
  const selected = useMemo(() => new Set(parseCsvEmails(csv)), [csv]);
  const verifiedOnly = (verifiedEmails || []).filter((e) => e.verified);

  if (verifiedOnly.length === 0) {
    return (
      <div className="small text-muted fst-italic">
        Sin emails verificados. Agregá y verificá emails arriba para elegir destinatarios.
      </div>
    );
  }

  // Usamos <span> plano en vez de <Badge> de Reactstrap: el Badge agrega
  // clases de Bootstrap (bg-secondary, text-*) con más especificidad que
  // nuestro style inline y pintan mal el contraste.
  return (
    <div>
      <div className="small text-muted mb-2">{label}</div>
      <div className="d-flex flex-wrap gap-2">
        {verifiedOnly.map((e) => {
          const isSelected = selected.has(e.email.toLowerCase());
          const displayName = e.alias ? `${e.alias} (${e.email})` : e.email;
          return (
            <span
              key={e.id}
              onClick={() => canEdit && onToggle(e.email)}
              style={{
                cursor: canEdit ? "pointer" : "default",
                padding: "7px 14px",
                fontSize: 13,
                fontWeight: 500,
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                lineHeight: 1.2,
                userSelect: "none",
                backgroundColor: isSelected ? "#50a5f1" : "#f1f5f9",
                color: isSelected ? "#ffffff" : "#475569",
                border: `1px solid ${isSelected ? "#50a5f1" : "#e2e8f0"}`,
                transition: "all 0.12s ease",
              }}
            >
              {isSelected && (
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: 16, marginRight: 4 }}
                >
                  check
                </span>
              )}
              {displayName}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default ConfigTab;
